/**
 * Form helpers for create/edit establishment views.
 */
import type { ClassDraft } from '@/components/forms/ClassesGrid';
import type {
  CreateEstablishmentClassDto,
  CreateEstablishmentDto,
  Establishment,
  EstablishmentCategory,
  EstablishmentFormState,
  EstablishmentContact,
  EstablishmentPriorityNetwork,
  EstablishmentStatus,
  EstablishmentType,
  PartnerRelationshipQuality,
  ProspectionStatus,
  UpdateEstablishmentDto,
  Classroom,
} from '@/types/establishment.types';
import { parseOptionalQuotaValue } from './establishment-table-utils';
import {
  ESTABLISHMENT_TYPES_WITH_CLASSES,
  LEGACY_CONTACT_FIELDS,
  PROSPECTION_STATUS_CONFIG,
} from './establishment-configs';
import type { AddressSelectionResult } from '@/types/geography.types';
import { postalCodeToDepartmentCode } from '@/utils/department-lookup-utils';
import { formatPersonName as formatSharedPersonName } from '@/utils/common-utils';
import { getCompanyCountry } from '@/stores/themeStore';

/** {@link Select} yes/no/unset stored as strings so empty {@link Select} works. */
export type TriState = '' | 'true' | 'false';
/** Editable prospection values; `''` means "to_define" / locked. */
export type EstablishmentFormProspectionStatus = '' | 'en_negociation' | 'valide' | 'refuse';

/** Current user role used to lock `managedById` for managers. */
export type EstablishmentFormActor = {
  isManager: boolean;
  currentUserId?: string;
};

/** First + last, then email, then id / fallback. */
export function formatPersonName(
  person: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    id?: string;
  },
  fallback = '—',
): string {
  return formatSharedPersonName(person, fallback);
}

/** Keep legacy contact* form fields in sync with {@link ContactsGrid} (used by validation schema). */
function primaryContactLegacyFields(contacts: EstablishmentContact[]): {
  contactName: string;
  contactEmail: string;
  contactPhone: string;
} {
  const primary =
    contacts.find(
      (c) => c.contactName?.trim() || c.contactEmail?.trim() || c.contactPhone?.trim(),
    ) ?? contacts[0];
  if (!primary) {
    return { contactName: '', contactEmail: '', contactPhone: '' };
  }
  return {
    contactName: primary.contactName || '',
    contactEmail: primary.contactEmail || '',
    contactPhone: primary.contactPhone || '',
  };
}
import { getErrorMessage } from '@/utils/errors-utils';

export type { EstablishmentFormState };

/** Fallback when the field is empty or not a number. */
const DEFAULT_MAX_PAST_DAYS = 7;
/** Hard cap so a typo cannot store an unbounded past-days window. */
const MAX_PAST_DAYS_CAP = 365;

/** Blank contact row used as the create-form seed. */
const EMPTY_CONTACT: EstablishmentContact = {
  contactName: '',
  contactEmail: '',
  contactPhone: '',
};

/** Shared empty form; create starts with one blank contact, edit starts with none. */
function emptyEstablishmentForm(
  managedById: string,
  contacts: EstablishmentContact[],
): EstablishmentFormState {
  return {
    uaiCode: '',
    name: '',
    officialName: '',
    type: '',
    establiCategory: '',
    priorityNetwork: '',
    status: 'active',
    addressLine1: '',
    department: '',
    departmentCode: '',
    postalCode: '',
    city: '',
    country: getCompanyCountry(),
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    contacts,
    volunteerQuota: '',
    studentQuota: '',
    volunteersToRecruit: '',
    maxPastDays: '7',
    prospectMeetingsCount: 0,
    ipsValue: '',
    isQpv: '',
    renewalNextYear: '',
    hasDevoirsFaits: '',
    hasInternat: '',
    involvementLevel: '',
    partnerRelationshipQuality: '',
    conventionSigned: '',
    prospectionStatus: '',
    engagementStartDate: '',
    engagementEndDate: '',
    managedById,
    coordinatorIds: [],
    classrooms: [],
    classes: [],
    offeredSpecialtyIds: [],
  };
}

/** Empty create form; managers pass their user id as `managedById`. */
export function emptyCreateEstablishmentForm(managedById = ''): EstablishmentFormState {
  return emptyEstablishmentForm(managedById, [{ ...EMPTY_CONTACT }]);
}

/** Empty edit form (hydrated later by {@link establishmentToFormData}). */
export function emptyEditEstablishmentForm(): EstablishmentFormState {
  return emptyEstablishmentForm('', []);
}

/** Map a {@link Select} tri-state to API boolean (`''` → omit). */
export function triStateToBool(value: TriState): boolean | undefined {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

/** Map API boolean / null to a {@link Select} tri-state. */
export function boolToTriState(value: boolean | null | undefined): TriState {
  if (value === true) return 'true';
  if (value === false) return 'false';
  return '';
}

/** Patch one field and apply side effects (clear coordinators / classes, sync legacy contacts). */
export function applyEstablishmentFormField(
  form: EstablishmentFormState,
  field: string,
  value: unknown,
): EstablishmentFormState {
  const updates: Record<string, unknown> = { [field]: value };

  if (field === 'managedById' && value !== form.managedById) {
    updates.coordinatorIds = [];
  }

  if (field === 'type' && !ESTABLISHMENT_TYPES_WITH_CLASSES.has(value as string)) {
    updates.classes = [];
  }

  if (field === 'contacts' && Array.isArray(value)) {
    Object.assign(updates, primaryContactLegacyFields(value as EstablishmentContact[]));
  }

  if (field === 'uaiCode' && typeof value === 'string') {
    updates.uaiCode = value.toUpperCase();
  }

  return { ...form, ...updates };
}

/** Apply several establishment fields; later keys overwrite. */
export function applyEstablishmentFormPatch(
  form: EstablishmentFormState,
  patch: Partial<EstablishmentFormState>,
): EstablishmentFormState {
  let next = form;
  for (const [key, value] of Object.entries(patch)) {
    next = applyEstablishmentFormField(next, key, value);
  }
  return next;
}

/** Code from `"Name (Code)"` or a bare department code (`75`, `2A`, `971`). */
function parseDepartmentCodeFromText(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  const open = trimmed.lastIndexOf('(');
  const close = trimmed.lastIndexOf(')');
  const token =
    open >= 0 && close === trimmed.length - 1 && open < close
      ? trimmed.slice(open + 1, close)
      : trimmed;
  if (token === '2A' || token === '2B' || token === '2a' || token === '2b') {
    return token.toUpperCase();
  }
  if (/^\d{2,3}$/.test(token)) return token;
  return undefined;
}

/** {@link Select} value on edit: API `departmentCode`, then label/code text, then postal. */
export function resolveEstablishmentFormDepartmentCode(establishment: {
  departmentCode?: string | null;
  department?: string | null;
  postalCode?: string | null;
}): string {
  const fromApi = establishment.departmentCode?.trim();
  if (fromApi) return fromApi;
  const fromText = parseDepartmentCodeFromText(establishment.department);
  if (fromText) return fromText;
  return postalCodeToDepartmentCode(establishment.postalCode ?? '') ?? '';
}

/** BAN selection → address + department code (keeps a display label in `department`). */
export function establishmentAddressFromAutocomplete(
  result: AddressSelectionResult,
): Partial<EstablishmentFormState> {
  return {
    addressLine1: result.addressLine1,
    postalCode: result.postalCode,
    city: result.city,
    departmentCode: result.departmentCode,
    country: result.country || getCompanyCountry(),
    ...(result.department ? { department: result.department } : {}),
  };
}

/** {@link DatePicker} value as `YYYY-MM-DD` (empty when unset). */
export function toIsoDateInput(value: Date | string | null | undefined): string {
  if (!value) return '';
  return new Date(value).toISOString().split('T')[0];
}

/** `to_define` is not an editable {@link Select} value — show as empty. */
function resolveEditProspectionStatus(
  status: ProspectionStatus | undefined,
): EstablishmentFormProspectionStatus {
  if (!status || status === 'to_define') return '';
  return status;
}

/** Hydrate the edit form from GET establishment + classrooms + classes. */
export function establishmentToFormData(
  establishment: Establishment,
  existingClassrooms: Classroom[],
  existingClasses: ClassDraft[],
): EstablishmentFormState {
  const contacts =
    establishment.contacts && establishment.contacts.length > 0 ? establishment.contacts : [];
  const legacyContacts =
    contacts.length > 0
      ? primaryContactLegacyFields(contacts)
      : {
          contactName: establishment.contactName || '',
          contactEmail: establishment.contactEmail || '',
          contactPhone: establishment.contactPhone || '',
        };

  return {
    uaiCode: establishment.uaiCode || '',
    name: establishment.name || '',
    officialName: establishment.officialName || '',
    type: (establishment.type || '') as '' | EstablishmentType,
    establiCategory: (establishment.establiCategory || '') as '' | EstablishmentCategory,
    priorityNetwork: (establishment.priorityNetwork || '') as '' | EstablishmentPriorityNetwork,
    ipsValue: establishment.ipsValue ?? '',
    isQpv: boolToTriState(establishment.isQpv),
    renewalNextYear: boolToTriState(establishment.renewalNextYear),
    hasDevoirsFaits: boolToTriState(establishment.hasDevoirsFaits),
    hasInternat: boolToTriState(establishment.hasInternat),
    involvementLevel:
      establishment.involvementLevel != null ? String(establishment.involvementLevel) : '',
    partnerRelationshipQuality: establishment.partnerRelationshipQuality || '',
    conventionSigned: boolToTriState(establishment.conventionSigned),
    prospectionStatus: resolveEditProspectionStatus(establishment.prospectionStatus),
    prospectMeetingsCount: establishment.prospectMeetingsCount ?? 0,
    status: establishment.status as EstablishmentStatus,
    addressLine1: establishment.addressLine1 || '',
    department: establishment.department || '',
    departmentCode: resolveEstablishmentFormDepartmentCode(establishment),
    postalCode: establishment.postalCode || '',
    city: establishment.city || '',
    country: establishment.country || getCompanyCountry(),
    contactName: legacyContacts.contactName,
    contactEmail: legacyContacts.contactEmail,
    contactPhone: legacyContacts.contactPhone,
    contacts,
    volunteerQuota: establishment.volunteerQuota ?? '',
    studentQuota: establishment.studentQuota ?? '',
    volunteersToRecruit: establishment.volunteersToRecruit ?? '',
    maxPastDays: typeof establishment.maxPastDays === 'number' ? establishment.maxPastDays : 7,
    engagementStartDate: toIsoDateInput(establishment.engagementStartDate),
    engagementEndDate: toIsoDateInput(establishment.engagementEndDate),
    managedById: establishment.managedById || establishment.managedBy?.id || '',
    coordinatorIds:
      establishment.coordinators?.map((coordinator) => coordinator.id) ||
      establishment.coordinatorIds ||
      [],
    classrooms: existingClassrooms,
    classes: existingClasses,
    offeredSpecialtyIds: establishment.offeredSpecialties?.map((specialty) => specialty.id) ?? [],
  };
}

/** Drop blank contact rows before sending to the API. */
function mapValidContacts(contacts: EstablishmentContact[]): EstablishmentContact[] {
  return contacts
    .filter(
      (contact) =>
        contact.contactName?.trim() || contact.contactEmail?.trim() || contact.contactPhone?.trim(),
    )
    .map((contact) => ({
      contactName: contact.contactName || undefined,
      contactEmail: contact.contactEmail || undefined,
      contactPhone: contact.contactPhone || undefined,
      fonction: contact.fonction || undefined,
    }));
}

/** Map class drafts to DTOs; `includeId` keeps existing ids on update. */
function mapClassDrafts(classes: ClassDraft[], includeId: boolean): CreateEstablishmentClassDto[] {
  return classes
    .filter((draft) => draft.name.trim() && draft.levelId && draft.schoolYearId)
    .map((draft) => ({
      ...(includeId && draft.id ? { id: draft.id } : {}),
      name: draft.name.trim(),
      levelId: draft.levelId,
      schoolYearId: draft.schoolYearId,
      studentCount: draft.studentCount,
    }));
}

/** Parse max-past-days; invalid / empty falls back, values are capped. */
function parseMaxPastDays(value: string | number): number {
  const parsed = Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) && parsed >= 0
    ? Math.min(parsed, MAX_PAST_DAYS_CAP)
    : DEFAULT_MAX_PAST_DAYS;
}

/** Empty string → omit from the DTO. */
function optionalNumber(value: string | number | undefined): number | undefined {
  if (value === '' || value === undefined) return undefined;
  return Number(value);
}

/** Managers cannot pick another manager — force their own user id. */
function resolveManagedById(
  form: EstablishmentFormState,
  actor: EstablishmentFormActor,
): string | undefined {
  return actor.isManager ? actor.currentUserId : form.managedById || undefined;
}

/** Fields shared by create and update DTOs. */
function buildSharedEstablishmentDto(
  form: EstablishmentFormState,
  actor: EstablishmentFormActor,
): Omit<CreateEstablishmentDto, 'uaiCode' | 'coordinatorIds' | 'classes'> {
  const validContacts = mapValidContacts(form.contacts);
  return {
    name: form.name,
    officialName: form.officialName || undefined,
    type: form.type || undefined,
    establiCategory: (form.establiCategory as EstablishmentCategory) || undefined,
    priorityNetwork: (form.priorityNetwork as EstablishmentPriorityNetwork) || undefined,
    ipsValue: optionalNumber(form.ipsValue),
    isQpv: triStateToBool(form.isQpv),
    renewalNextYear: triStateToBool(form.renewalNextYear),
    hasDevoirsFaits: triStateToBool(form.hasDevoirsFaits),
    hasInternat: triStateToBool(form.hasInternat),
    involvementLevel: form.involvementLevel ? Number(form.involvementLevel) : undefined,
    partnerRelationshipQuality:
      (form.partnerRelationshipQuality as PartnerRelationshipQuality) || undefined,
    conventionSigned: triStateToBool(form.conventionSigned),
    status: form.status,
    addressLine1: form.addressLine1 || undefined,
    department: form.department || undefined,
    departmentCode: form.departmentCode || undefined,
    postalCode: form.postalCode || undefined,
    city: form.city || undefined,
    country: form.country || undefined,
    contactName: form.contactName || undefined,
    contactEmail: form.contactEmail || undefined,
    contactPhone: form.contactPhone || undefined,
    contacts: validContacts.length > 0 ? validContacts : undefined,
    volunteerQuota: parseOptionalQuotaValue(form.volunteerQuota),
    studentQuota: parseOptionalQuotaValue(form.studentQuota),
    volunteersToRecruit: parseOptionalQuotaValue(form.volunteersToRecruit),
    maxPastDays: parseMaxPastDays(form.maxPastDays),
    prospectMeetingsCount: form.prospectMeetingsCount ? Number(form.prospectMeetingsCount) : 0,
    engagementStartDate: form.engagementStartDate || undefined,
    engagementEndDate: form.engagementEndDate || undefined,
    managedById: resolveManagedById(form, actor),
    rooms: form.classrooms,
  };
}

/** Create DTO: include UAI, omit empty coordinator list, classes without ids. */
export function buildCreateEstablishmentDto(
  form: EstablishmentFormState,
  actor: EstablishmentFormActor,
): CreateEstablishmentDto {
  return {
    ...buildSharedEstablishmentDto(form, actor),
    uaiCode: form.uaiCode,
    coordinatorIds: form.coordinatorIds.length > 0 ? form.coordinatorIds : undefined,
    classes: mapClassDrafts(form.classes, false),
  };
}

/** Update DTO: keep coordinator ids (even empty) and class ids. */
export function buildUpdateEstablishmentDto(
  form: EstablishmentFormState,
  actor: EstablishmentFormActor,
): UpdateEstablishmentDto {
  return {
    ...buildSharedEstablishmentDto(form, actor),
    prospectionStatus: form.prospectionStatus || undefined,
    coordinatorIds: form.coordinatorIds,
    classes: mapClassDrafts(form.classes, true),
  };
}

/** Validate class drafts for types that support classes (required fields + no name/year duplicates). */
export function getClassesError(
  type: string | undefined,
  classes: ClassDraft[] | undefined,
): string | null {
  if (!ESTABLISHMENT_TYPES_WITH_CLASSES.has(type ?? '')) return null;
  const list = classes ?? [];

  for (let i = 0; i < list.length; i++) {
    const draft = list[i];
    if (!draft.name?.trim()) {
      return getErrorMessage({ code: 'establishmentClassNameRequired', index: i + 1 });
    }
    if (!draft.levelId) {
      return getErrorMessage({
        code: 'establishmentClassLevelRequired',
        index: i + 1,
        name: draft.name,
      });
    }
    if (!draft.schoolYearId) {
      return getErrorMessage({
        code: 'establishmentClassSchoolYearRequired',
        index: i + 1,
        name: draft.name,
      });
    }
  }

  const seen = new Map<string, number>();
  for (let i = 0; i < list.length; i++) {
    const key = `${list[i].name.trim().toLowerCase()}::${list[i].schoolYearId}`;
    if (seen.has(key)) {
      return getErrorMessage({
        code: 'establishmentClassDuplicate',
        name: list[i].name.trim(),
        lineA: seen.get(key)! + 1,
        lineB: i + 1,
      });
    }
    seen.set(key, i);
  }
  return null;
}

/** Display name for a manager / coordinator {@link Select} option. */
export function formatEstablishmentPersonLabel(person: {
  id?: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}): string {
  return (
    `${person.firstName || ''} ${person.lastName || ''}`.trim() || person.email || person.id || ''
  );
}

/** Helper under the coordinators {@link MultiSelect} once a manager is chosen. */
export function getCoordinatorsHelperText(): string {
  return 'Optionnel - Coordinateurs qui gèrent cet établissement';
}

/** Helper under the coordinators {@link MultiSelect} while it stays disabled. */
export function getCoordinatorsLockedHelperText(): string {
  return getErrorMessage('firstSelectManager');
}

/** {@link Select} options from the API's editable prospection statuses. */
export function mapEditableProspectionOptions(
  statuses: ProspectionStatus[] | undefined,
): Array<{ value: string; label: string }> {
  return (statuses ?? []).map((status) => ({
    value: status,
    label: PROSPECTION_STATUS_CONFIG[status]?.label ?? status,
  }));
}

/** Re-validate legacy contact* fields when the contacts grid changes. */
export function revalidateLegacyContactFields(
  field: string,
  value: unknown,
): readonly (typeof LEGACY_CONTACT_FIELDS)[number][] {
  if (field === 'contacts' && Array.isArray(value)) return LEGACY_CONTACT_FIELDS;
  return [];
}

/** Central error copy from {@link getErrorMessage}. */
export function getEstablishmentFormErrorMessage(error: unknown, _fallback?: string): string {
  return getErrorMessage(error);
}

/** Read-only current prospection label (used when the status is locked). */
export function currentProspectionLabel(status: ProspectionStatus | undefined): string {
  return PROSPECTION_STATUS_CONFIG[status ?? 'to_define']?.label ?? 'À définir';
}
